//LIBRERIES
import { computed, createApp, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { i18n } from '../lang/index';
import orderBy from "lodash/orderBy";
import { useI18n } from 'vue-i18n';
import moment from "../node_modules/moment/moment";

//MODELS
import { App } from "../domain/App";
import { Cookie } from "../domain/Cookie";
import { InscriptionDocumentOrigin } from "../domain/enum/InscriptionDocumentOrigin";
import { InscriptionStatus } from "../domain/enum/InscriptionStatus";
import { Languages } from "../domain/enum/Languages";
import { InscriptionType } from "../domain/enum/InscriptionType";
import { Constants } from "../domain/enum/Constants";
import { MyInscription } from "../domain/entities/myInscription/MyInscription";
import ICommonDataState from '../domain/interfaces/ICommonDataState';

//APISERVICES
import { getInscriptionsDni, getProcessInscriptionsData } from "../infrastructure/apiServices/myInscription.service";
import { getEntities } from "../infrastructure/apiServices/process.service";
import DocumentService from "./services/DocumentService";
import { getProcessesConfigurationGroup, getHeaderConfiguration, getClientAvailableLanguajes, getFeatureFlag } from "../infrastructure/apiServices/masterData.service";

//COMPOSABLES
import { useCommonData } from "./composables/useCommonData";
import { useIncidences } from "./composables/useIncidences";

export const MyInscriptionsComponent = defineComponent({
  setup() {
    // dependencies
    const { t, locale } = useI18n();

    // states
    const commonDataState = reactive({
      headerConfig: {},
      availableLanguajes: { defaultLanguage: '', secondLanguage: '' },
      cookie: new Cookie(),
      app: new App(),
      locale
    });

    // computed properties
    const currentLanguage = computed(() => commonDataState.locale);
    const lastLogin = computed(() => moment(commonDataState.app.profile.lastLogin[0]).format(commonDataState.app.formatDate) + ' ' + moment.utc(commonDataState.app.profile.lastLogin[0]).local().format('HH:mm:ss'));
    const hasTwoTypeInscriptions = computed(() => filterInscriptionCandidate.value.length > 0 && filterInscriptionRepresentative.value.length > 0);
    const filterInscriptionCandidate = computed(() => myInscriptionsList.value.filter(inscription => inscription.represented.nif.toUpperCase() === commonDataState.app.profile.nickname.toUpperCase()))
    const filterInscriptionRepresentative = computed(() => myInscriptionsList.value.filter(inscription => inscription.represented.nif.toUpperCase() !== commonDataState.app.profile.nickname.toUpperCase()))
    const showRepresentativeInscription = computed(() => filterInscriptionRepresentative.value.length > 0 && (filterInscriptionCandidate.value.length <= 0 || tabSelected.value === InscriptionType.enum.REPRESENTED));

    // shared properties
    const imageClass = 'applicant-hero-image--replacement-list';
    const isLoading = ref(false);
    const showMandatoryCookies = ref(false);
    const showAnalysisCookies = ref(false);
    const showModalConfigurateCookies = ref(false);
    const showServiceStopModal = ref(false);
    const dontShowAgainStopServiceModalAgain = ref(false);
    const serviceStopLocalStorageKey = 'dontShowServiceStopModal';
    const serviceStopEndDate = moment('2024-05-15T17:00:00');
    const isCompositionApi = true;

    //view properties
    const myInscriptionsList = ref([]);
    const inscriptionFile = ref('');
    const tabSelected = ref(InscriptionType.enum.CANDIDATE);
    const isRedirecting = ref(false);

    //methods
    const changeLocal = (language: string) => {
      commonDataState.locale = language;
      commonDataState.app.changeLanguaje(language);
    };

    const saveCookies = () => {
      commonDataState.cookie.savePersonalizateCookies();
      showModalConfigurateCookies.value = false;
    };

    const closeModalConfigurateCookies = () => {
      showModalConfigurateCookies.value = false;
      showMandatoryCookies.value = false;
      showAnalysisCookies.value = false;
    };

    const acceptAllCookies = () => {
      commonDataState.cookie.acceptAllCookies();
    };

    const declineAllCookies = () => {
      commonDataState.cookie.declineAllCookies();
    };

    const sortByDocumentOrigin = (documents) => {
      return orderBy(documents, 'order').filter((document) => document.origin !== InscriptionDocumentOrigin.enum.REPRESENTATIVEAUTHORIZATION);
    };

    const getInscriptions = () => {
      isLoading.value = true;
      Promise.all([getInscriptionsDni(commonDataState.app.profile.nickname), getEntities(), getProcessesConfigurationGroup()])
        .then((response: any) => {
          var inscriptions = response[0].data;
          var entities = response[1].data;
          var processConfigurationGroups = response[2].data;
          asyncForEach(inscriptions, async (element) => {
            var myInscription = new MyInscription(element);
            await getProcessInscriptionsData(commonDataState.app.profile.nickname, element.inscriptionId)
              .then((responseProcess: any) => {
                myInscription.setProcessData(responseProcess.data, processConfigurationGroups);
                myInscription.setEntity(entities);
                myInscriptionsList.value.push(myInscription);
              }).catch((error) => {
                console.error(error);
              });
          })
            .then(() => {
              isLoading.value = false;
            }).catch((error) => {
              console.error(error);
            });
        }).catch((error) => {
          console.error(error);
        });
    };

    const asyncForEach = async (array, callback) => {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    }

    const getUrlCanCorrect = (id: string, type: number) => {
      location.href = 'correctInscription.html?nif=' + commonDataState.app.profile.nickname + '&inscriptionId=' + id + "&type=" + type;
    };

    const getUrlMeritsIncidents = (id: string) => {
      location.href = 'meritsIncidents.html?nif=' + commonDataState.app.profile.nickname + '&inscriptionId=' + id;
    };

    const goToInscription = (inscription: MyInscription) => {
      if (inscription.represented.nif.toUpperCase() !== commonDataState.app.profile.nickname.toUpperCase()) {
        location.href = 'inscription.html?id=' + inscription.processId + '&type=' + inscription.processType + '&nif=' + inscription.represented.nif.toUpperCase() + '&firstSurname=' + inscription.represented.firstSurname + '&secondSurname=' + inscription.represented.secondSurname + '&name=' + inscription.represented.name;
      } else {
        location.href = 'inscription.html?id=' + inscription.processId + '&type=' + inscription.processType;
      }
    };

    const goToDetail = (id: string, type: number) => {
      location.href = "inscriptionDetail.html?id=" + id + "&nif=" + commonDataState.app.profile.nickname + "&type=" + type;
    };

    const getAllData = () => {
      commonDataState.app.getProfile().then(() => {
        if (commonDataState.app.profile) {
          isRedirecting.value = false;
          getInscriptions();
        } else {
          isRedirecting.value = true;
          commonDataState.app.login();
        }
      }).catch((error) => {
        console.error(error);
      });
    };

    const downloadDocument = (document) => {
      DocumentService.download(document);
    };

    const updateCommonData = (state: ICommonDataState) => {
      commonDataState.availableLanguajes = state.availableLanguajes;
      commonDataState.cookie = state.cookie;
      commonDataState.headerConfig = state.headerConfig;
      commonDataState.app = state.app;
      commonDataState.locale = state.locale;

      getAllData();
    };


    // composables
    const useCommonDataDependencies = {
      updateCommonData,
      getHeaderConfiguration,
      getFeatureFlag,
      getClientAvailableLanguajes,
    };
    const { loadCommonData } = useCommonData(useCommonDataDependencies);
    const { incidence, showNotificationIncidence, IncidencesTypes, closeIncidence} = useIncidences();
    // lifecycle hooks
    onMounted(() => {
      loadCommonData();
      showServiceStopModal.value = commonDataState.app.isServiceStopActive && (!localStorage.getItem(serviceStopLocalStorageKey));
    });

    return {
      // dependencies
      t,
      Languages: Languages,
      // states
      ...toRefs(commonDataState),
      // shared view properties
      showServiceStopModal,
      showModalConfigurateCookies,
      dontShowAgainStopServiceModalAgain,
      serviceStopEndDate,
      showMandatoryCookies,
      showAnalysisCookies,
      imageClass,
      isCompositionApi,
      // computed properties
      lastLogin,
      pageTitle: '',
      currentLanguage,
      hasTwoTypeInscriptions,
      showRepresentativeInscription,
      filterInscriptionRepresentative,
      filterInscriptionCandidate,
      // methods
      getAllData,
      saveCookies,
      closeModalConfigurateCookies,
      acceptAllCookies,
      declineAllCookies,
      changeLocal,
      sortByDocumentOrigin,
      getUrlCanCorrect,
      getUrlMeritsIncidents,
      goToInscription,
      goToDetail,
      downloadDocument,
      getInscriptions,
      closeIncidence,
      // view properties
      isLoading,
      inscriptionFile,
      myInscriptionsList,
      InscriptionType,
      InscriptionDocumentOrigin,
      InscriptionStatus,
      isRedirecting,
      tabSelected,
      incidence,
      showNotificationIncidence,
      IncidencesTypes
    }
  }
});

const vueApp = createApp(MyInscriptionsComponent);
vueApp.use(i18n);
vueApp.config.compilerOptions.whitespace = 'preserve';
vueApp.mount(Constants.APP);